import { ChangeDetectionStrategy, Component, inject, InjectionToken } from '@angular/core';
import { IconComponent } from '@gains-com/ui-angular';
import { DarkenHexColorPipe } from '../../pipes/darken-hex-color.pipe';
import { LevelUpRewardDialogService } from '../../pages/vip/dialogs/level-up-reward-dialog/level-up-reward-dialog.service';

export interface VipLevelUpToastData {
  /** Heading, e.g. "Level Up Reward". */
  title: string;
  /** Sub-line, e.g. "Claim your Bronze Reward". */
  subtitle: string;
  /** Tier accent color (hex) — drives the colored badge panel. */
  color: string;
  /** Tier logo to render inside the badge; falls back to a VIP icon. */
  logoUrl: string | null;
  /** Config id of the tier just reached — opens that tier's reward on click. */
  vipLevelConfigId: string;
}

/** Lets the toast dismiss the overlay that hosts it (provided per-instance). */
export interface VipLevelUpToastHandle {
  dismiss(): void;
}

export const VIP_LEVEL_UP_TOAST_DATA = new InjectionToken<VipLevelUpToastData>('VIP_LEVEL_UP_TOAST_DATA');
export const VIP_LEVEL_UP_TOAST_HANDLE = new InjectionToken<VipLevelUpToastHandle>('VIP_LEVEL_UP_TOAST_HANDLE');

/**
 * Level-up toast. The Figma "Level Up" notification: a tier-colored badge
 * panel beside a title/subtitle, with a close affordance. Rendered in a global
 * CDK overlay by {@link VipLevelUpNotificationService} so it appears on any
 * page when a `VipLevelUp` realtime event arrives. Clicking the body opens the
 * level-up reward dialog in place (no navigation) so an in-game level-up
 * doesn't exit the player's game; the colors and icon come from the tier's
 * `vip_level_config`.
 */
@Component({
  selector: 'app-vip-level-up-toast',
  templateUrl: './vip-level-up-toast.component.html',
  styleUrl: './vip-level-up-toast.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, DarkenHexColorPipe],
})
export class VipLevelUpToastComponent {
  private readonly _levelUpRewardDialog = inject(LevelUpRewardDialogService);
  private readonly _handle = inject(VIP_LEVEL_UP_TOAST_HANDLE);

  readonly data = inject(VIP_LEVEL_UP_TOAST_DATA);

  open(): void {
    // Open the reward dialog for the tier just reached, over the current screen
    // rather than navigating to /vip — an in-game level-up shouldn't kick the
    // player out of their game.
    void this._levelUpRewardDialog.openLatest(this.data.vipLevelConfigId);
    this._handle.dismiss();
  }

  close(event: Event): void {
    event.stopPropagation();
    this._handle.dismiss();
  }
}
